import './styles.css';
import { createOidcSettings } from '@nwm-oss/auth-react/oidc-identity';
import { OidcCallbackPage } from '@nwm-oss/auth-react/shared-pages';
import { ProtectedRoute } from '@nwm-oss/auth-react/protected-route';
import { resolveWorkspaceRuntime } from '@nwm-oss/auth-react/runtime';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { OidcOnlyWorkspaceAuthProvider } from './auth/OidcOnlyWorkspaceAuthProvider';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { InboundMessagesPage } from './pages/InboundMessagesPage';
import { InboundMessageDetailPage } from './pages/InboundMessageDetailPage';
import { ExchangeDetailPage } from './pages/ExchangeDetailPage';
import { ExchangesPage } from './pages/ExchangesPage';
import { As2CertificatesPage } from './pages/As2CertificatesPage';
import { As2TransportProfilesPage } from './pages/As2TransportProfilesPage';
import { As2DlqPage } from './pages/As2DlqPage';
import { As2DashboardPage } from './pages/As2DashboardPage';
import { As2OutboundMessagesPage } from './pages/As2OutboundMessagesPage';
import { As2OutboundMessageDetailPage } from './pages/As2OutboundMessageDetailPage';
import { As2InboundMessagesPage } from './pages/As2InboundMessagesPage';
import { As2InboundMessageDetailPage } from './pages/As2InboundMessageDetailPage';
import { GeneratePage } from './pages/GeneratePage';
import { ComposeRawMessagePage } from './pages/ComposeRawMessagePage';
import { FlowsPage } from './pages/FlowsPage';
import { MaritimeExchangeDashboardPage } from './pages/MaritimeExchangeDashboardPage';
import { CusdecMlPage } from './pages/CusdecMlPage';
import { EtatChargementPage } from './pages/EtatChargementPage';
import { TryptiquePage } from './pages/TryptiquePage';
import { ScannerPage } from './pages/ScannerPage';
import { AmpPage } from './pages/AmpPage';
import { OperationsCenterPage } from './pages/OperationsCenterPage';
import { ExecutionsConsolePage } from './pages/ExecutionsConsolePage';
import { SettingsConsolePage } from './pages/SettingsConsolePage';
import { NotFoundPage } from './pages/NotFoundPage';
import {
  ConnectorsConsolePage,
  CuscarDemoFlowPage,
  DlqReplayPage,
  EaiDashboardPage,
  FunctionalTestLabPage,
  IntegrationFlowBuilderPage,
  MappingDesignerPage,
  MessageSchemaManagerPage,
  PartnerOnboardingWizardPage,
  PublishCenterPage,
} from './pages/EaiConsolePages';
import {
  ConnectorDefinitionsPage,
  ConnectorInstancesPage,
  InboundPoliciesPage,
  MappingsPage,
  PartnersPage,
  TemplatesPage,
  ValidationRulesPage,
} from './pages/ConfigPages';
import { demoFeaturesEnabled } from './runtime-features';

function readRuntimeConfig(key: string, fallback = ''): string {
  const windowConfig = typeof window === 'undefined'
    ? undefined
    : (window as Window & { __APP_CONFIG__?: Partial<Record<string, string>> }).__APP_CONFIG__;
  const configuredValue = windowConfig?.[key];

  if (typeof configuredValue === 'string' && configuredValue.length > 0) {
    return configuredValue;
  }

  return fallback;
}

function WorkspaceRoutes() {
  const location = useLocation();
  const runtime = resolveWorkspaceRuntime({
    search: location.search,
    hostname: typeof window === 'undefined' ? 'localhost' : window.location.hostname,
  });
  const showDemoFeatures = demoFeaturesEnabled();

  const oidcSettings = createOidcSettings({
    authority: readRuntimeConfig(
      'VITE_OIDC_AUTHORITY',
      import.meta.env.VITE_OIDC_AUTHORITY ?? 'http://localhost:8080/realms/nwm',
    ),
    clientId: readRuntimeConfig(
      'VITE_OIDC_CLIENT_ID',
      import.meta.env.VITE_OIDC_CLIENT_ID ?? 'oss-integration',
    ),
    audience: readRuntimeConfig(
      'VITE_OIDC_AUDIENCE',
      import.meta.env.VITE_OIDC_AUDIENCE ?? 'oss-integration',
    ),
    redirectUri: readRuntimeConfig(
      'VITE_OIDC_REDIRECT_URI',
      import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${window.location.origin}/auth/callback`,
    ),
    postLogoutRedirectUri: readRuntimeConfig(
      'VITE_OIDC_POST_LOGOUT_REDIRECT_URI',
      import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI ?? `${window.location.origin}/`,
    ),
    scope: readRuntimeConfig(
      'VITE_OIDC_SCOPE',
      import.meta.env.VITE_OIDC_SCOPE ?? 'openid profile email',
    ),
  });

  return (
    <OidcOnlyWorkspaceAuthProvider
      runtime={runtime.mode}
      search={oidcOnlySearch(location.search)}
      oidcSettings={oidcSettings}
      oidcClientId={oidcSettings.client_id}
    >
      {location.pathname === '/auth/callback' ? (
        <OidcCallbackPage fallbackPath="/dashboard" />
      ) : (
        <Layout>
          <Routes>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ProtectedRoute anyOf={['flow.access']}><EaiDashboardPage /></ProtectedRoute>} />
            {/* Production-ready EAI Studio tools — always available */}
            <Route path="flows/builder" element={<ProtectedRoute anyOf={['flow.operations.execute']}><IntegrationFlowBuilderPage /></ProtectedRoute>} />
            <Route path="flows/publish" element={<ProtectedRoute anyOf={['flow.operations.execute']}><PublishCenterPage /></ProtectedRoute>} />
            <Route path="schemas" element={<ProtectedRoute anyOf={['flow.operations.execute']}><MessageSchemaManagerPage /></ProtectedRoute>} />
            <Route path="mapping-designer" element={<ProtectedRoute anyOf={['flow.operations.execute']}><MappingDesignerPage /></ProtectedRoute>} />
            <Route path="test-lab" element={<ProtectedRoute anyOf={['flow.operations.execute']}><FunctionalTestLabPage /></ProtectedRoute>} />
            {showDemoFeatures ? (
              <>
                <Route path="partners/onboarding" element={<ProtectedRoute anyOf={['flow.operations.execute']}><PartnerOnboardingWizardPage /></ProtectedRoute>} />
                <Route path="demo/cuscar" element={<ProtectedRoute anyOf={['flow.operations.execute']}><CuscarDemoFlowPage /></ProtectedRoute>} />
              </>
            ) : null}
            <Route path="executions" element={<ProtectedRoute anyOf={['flow.access']}><ExecutionsConsolePage /></ProtectedRoute>} />
            <Route path="dlq" element={<ProtectedRoute anyOf={['flow.access']}><DlqReplayPage /></ProtectedRoute>} />
            <Route path="connectors" element={<ProtectedRoute anyOf={['flow.operations.execute']}><ConnectorsConsolePage /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute anyOf={['flow.operations.execute']}><SettingsConsolePage /></ProtectedRoute>} />
            <Route path="documents" element={<ProtectedRoute anyOf={['flow.access']}><DocumentsPage /></ProtectedRoute>} />
            <Route path="documents/:id" element={<ProtectedRoute anyOf={['flow.access']}><DocumentDetailPage /></ProtectedRoute>} />
            <Route path="inbound-messages" element={<ProtectedRoute anyOf={['flow.access']}><DocumentsPage initialView="inbound" /></ProtectedRoute>} />
            <Route path="inbound-messages/:id" element={<ProtectedRoute anyOf={['flow.access']}><InboundMessageDetailPage /></ProtectedRoute>} />
            <Route path="operations/command-center" element={<ProtectedRoute anyOf={['flow.access']}><OperationsCenterPage /></ProtectedRoute>} />
            <Route path="operations" element={<Navigate to={{ pathname: '/operations/command-center', search: location.search }} replace />} />
            <Route path="operations/exchanges" element={<ProtectedRoute anyOf={['flow.access']}><ExchangesPage /></ProtectedRoute>} />
            <Route path="operations/exchanges/:id" element={<ProtectedRoute anyOf={['flow.access']}><ExchangeDetailPage /></ProtectedRoute>} />
            <Route path="flows" element={<ProtectedRoute anyOf={['flow.access']}><FlowsPage /></ProtectedRoute>} />
            {/* Canonical flow catalog route is /flows (linked from the sidebar); redirect the legacy /operations/flows path some links still target. */}
            <Route path="operations/flows" element={<Navigate to={{ pathname: '/flows', search: location.search }} replace />} />
            <Route path="operations/maritime" element={<ProtectedRoute anyOf={['flow.access']}><MaritimeExchangeDashboardPage /></ProtectedRoute>} />
            <Route path="operations/cusdec-ml" element={<ProtectedRoute anyOf={['flow.access']}><CusdecMlPage /></ProtectedRoute>} />
            <Route path="operations/scanner" element={<ProtectedRoute anyOf={['flow.access']}><ScannerPage /></ProtectedRoute>} />
            <Route path="operations/etat-chargement" element={<ProtectedRoute anyOf={['flow.access']}><EtatChargementPage /></ProtectedRoute>} />
            <Route path="operations/tryptique" element={<ProtectedRoute anyOf={['flow.access']}><TryptiquePage /></ProtectedRoute>} />
            <Route path="operations/amp" element={<ProtectedRoute anyOf={['flow.access']}><AmpPage /></ProtectedRoute>} />
            <Route path="generate" element={<ProtectedRoute anyOf={['flow.operations.execute']}><GeneratePage /></ProtectedRoute>} />
            <Route path="compose" element={<ProtectedRoute anyOf={['flow.operations.execute']}><ComposeRawMessagePage /></ProtectedRoute>} />
            <Route path="config/partners" element={<ProtectedRoute anyOf={['flow.operations.execute']}><PartnersPage /></ProtectedRoute>} />
            <Route path="config" element={<Navigate to={{ pathname: '/settings', search: location.search }} replace />} />
            <Route path="partners" element={<Navigate to={{ pathname: '/config/partners', search: location.search }} replace />} />
            <Route path="config/connectors" element={<ProtectedRoute anyOf={['flow.operations.execute']}><ConnectorDefinitionsPage /></ProtectedRoute>} />
            <Route path="config/connector-health" element={<ProtectedRoute anyOf={['flow.operations.execute']}><ConnectorInstancesPage /></ProtectedRoute>} />
            <Route path="config/inbound-policies" element={<ProtectedRoute anyOf={['flow.operations.execute']}><InboundPoliciesPage /></ProtectedRoute>} />
            <Route path="mappings" element={<ProtectedRoute anyOf={['flow.operations.execute']}><MappingsPage /></ProtectedRoute>} />
            <Route path="templates" element={<ProtectedRoute anyOf={['flow.operations.execute']}><TemplatesPage /></ProtectedRoute>} />
            <Route path="validation-rules" element={<ProtectedRoute anyOf={['flow.operations.execute']}><ValidationRulesPage /></ProtectedRoute>} />
            <Route path="config/mappings" element={<ProtectedRoute anyOf={['flow.operations.execute']}><MappingsPage /></ProtectedRoute>} />
            <Route path="config/templates" element={<ProtectedRoute anyOf={['flow.operations.execute']}><TemplatesPage /></ProtectedRoute>} />
            <Route path="config/validation-rules" element={<ProtectedRoute anyOf={['flow.operations.execute']}><ValidationRulesPage /></ProtectedRoute>} />
            <Route path="as2" element={<ProtectedRoute anyOf={['flow.access']}><As2DashboardPage /></ProtectedRoute>} />
            <Route path="as2/outbound-messages" element={<ProtectedRoute anyOf={['flow.access']}><As2OutboundMessagesPage /></ProtectedRoute>} />
            <Route path="as2/outbound-messages/:id" element={<ProtectedRoute anyOf={['flow.access']}><As2OutboundMessageDetailPage /></ProtectedRoute>} />
            <Route path="as2/inbound-messages" element={<ProtectedRoute anyOf={['flow.access']}><As2InboundMessagesPage /></ProtectedRoute>} />
            <Route path="as2/inbound-messages/:id" element={<ProtectedRoute anyOf={['flow.access']}><As2InboundMessageDetailPage /></ProtectedRoute>} />
            <Route path="as2/certificates" element={<ProtectedRoute anyOf={['flow.access']}><As2CertificatesPage /></ProtectedRoute>} />
            <Route path="as2/transport-profiles" element={<ProtectedRoute anyOf={['flow.access']}><As2TransportProfilesPage /></ProtectedRoute>} />
            <Route path="as2/dlq" element={<ProtectedRoute anyOf={['flow.access']}><As2DlqPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      )}
    </OidcOnlyWorkspaceAuthProvider>
  );
}

export function IntegrationAdminApp() {
  return <WorkspaceRoutes />;
}

export default IntegrationAdminApp;

function oidcOnlySearch(search: string): string {
  return search ? '' : search;
}
